<script>
export default {
  name: "ExpandableRows",
};
</script>

<script setup>
import { computed, ref, toRefs } from "vue";
import { PencilIcon } from "@heroicons/vue/24/outline";
import { ChevronRightIcon } from "@heroicons/vue/20/solid";
import { FlexRender } from "@tanstack/vue-table";
import Spinner from "@/components/helpers/Spinner";
import { COLUMNS_IDS } from "@/components/StudentFulfillment/components/OrderDetail/sections/Details/constants";
import TicketIcon from "@/components/svg-icons/LocalActivityTicketIcon";
import {
  EXCURSION_INCLUDED_TEXTS,
  LAST_TIME_UPDATED_LITERAL,
} from "@/components/StudentFulfillment/components/OrderDetail/sections/Details/constants";
import {
  usePriceOverrides,
} from "@/composables/authorization";

const EXCURSION_INCLUDED_FIELD_NAME = "excursionIncluded";

const props = defineProps({
  rows: {
    type: Array,
    default: () => [],
  },
  label: {
    type: String,
    default: "",
  },
  editEnabled: {
    type: Boolean,
    default: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  itemNameRouteSetup: {
    type: Object,
    default: () => {},
  },
});

const { rows, label, loading, editEnabled, itemNameRouteSetup } = toRefs(props);

const emit = defineEmits(["expanded", "openStatusModal"]);

const open = ref(false);

const expand = () => {
  if (rows.value.length) {
    open.value = !open.value;
    emit("expanded", open.value);
  }
};
</script>

<template>
  <tr
    class="border-t-[1px] border-solid border-t-gray-200 w-full hover:bg-blue-350"
    :class="[open && rows?.length ? 'bg-gray-200' : '']"
    @click="expand"
  >
    <th
      data-testid="expand-header"
      colspan="8"
      class="py-8 text-md border-t-[1px] border-solid border-t-gray-200"
    >
      <button
        :class="['text-md font-semibold flex items-center cursor-pointer']"
      >
        <ChevronRightIcon
          v-if="rows?.length"
          :class="[
            {
              'rotate-90 transform': open,
            },
            'h-6 w-6 inline-block',
          ]"
        />
        <div
          :class="[!rows?.length ? 'pl-4' : '']"
          class="flex justify-start items-center"
        >
          <span> {{ label }} ({{ rows?.length }})</span>
        </div>
      </button>
    </th>
    <th
      data-testid="edit-header"
      class="py-8 text-md border-t-[1px] border-solid border-t-gray-200"
    >
      <button
        v-if="!rows?.length"
        class="flex justify-center items-center ml-4 text-md font-bold bg-transparent text-teal-900"
        :disabled="!editEnabled"
        @click="emit('openStatusModal')"
      >
        <PencilIcon class="h-5" />
      </button>
    </th>
  </tr>
  <template v-for="(row, index) in rows || []">
    <tr v-if="open && !loading" :key="index" class="w-full hover:bg-blue-350">
      <td
        v-for="cell in row.getVisibleCells()"
        :key="cell.column.id"
        class="py-6 text-md first:pl-4 capitalize border-t-[1px] border-solid border-t-gray-200"
        :class="[
          cell.column.id.trim() === COLUMNS_IDS.name ? 'text-teal-900' : '',
        ]"
      >
        <button
          v-if="cell.column.id.trim() === COLUMNS_IDS.action"
          class="flex justify-center items-center ml-4 text-md font-bold bg-transparent text-teal-900"
          :disabled="!editEnabled"
          @click="emit('openStatusModal', row.original.id)"
        >
          <PencilIcon class="h-5" />
        </button>
        <div
          v-else-if="
            cell.column.id.trim() === COLUMNS_IDS.more_info &&
            cell.row.original[COLUMNS_IDS.more_info]
          "
          class="flex flex-col"
        >
          <span
            v-for="(item, key) in cell.row.original[COLUMNS_IDS.more_info]"
            :key="key"
            class="flex items-center"
          >
            <TicketIcon
              v-if="
                key === EXCURSION_INCLUDED_FIELD_NAME &&
                item === EXCURSION_INCLUDED_TEXTS.included
              "
            />
            {{ item }}
          </span>
        </div>
        <router-link
          v-else-if="
            cell.column.id.trim() === COLUMNS_IDS.name &&
            row.original?.id &&
            itemNameRouteSetup?.[label]?.name &&
            itemNameRouteSetup?.[label]?.permission
          "
          :to="{
            name: itemNameRouteSetup[label].name,
            params: {
              [itemNameRouteSetup[label].extraParamName]: row.original.id,
            },
          }"
          target="_blank"
        >
          {{ cell.row.original[COLUMNS_IDS.name] }}
        </router-link>
        <div
          v-else-if="
            cell.column.id.trim() === COLUMNS_IDS.totalPrice &&
            row.original?.priceOverrideStatus?.display
          "
          class="flex items-center gap-1"
        >
          <span>{{ row.original[COLUMNS_IDS.totalPrice] }}</span>
          <span
            v-if="usePriceOverrides"
            class="material-symbols-outlined"
            :class="row.original.priceOverrideStatus.labelColor"
          >
            {{ row.original.priceOverrideStatus.icon }}
          </span>
        </div>
        <FlexRender
          v-else-if="cell.column.id.trim() !== COLUMNS_IDS.type"
          :render="cell.column.columnDef.cell"
          :props="cell.getContext()"
        />
      </td>
    </tr>
    <tr
      v-if="row.original?.[LAST_TIME_UPDATED_LITERAL] && open && !loading"
      :key="`updateTime${index}`"
    >
      <td colspan="20" class="italic text-right pr-8">
        Last updated: {{ row.original?.[LAST_TIME_UPDATED_LITERAL] }}
      </td>
    </tr>
  </template>
  <tr v-if="open && loading && rows?.length">
    <td colspan="9">
      <Spinner class="w-full py-2 relative" />
    </td>
  </tr>
</template>
